import { Search } from "@mui/icons-material";
import {
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { useUpdateThreadMutation } from "src/hooks";
import { useAgentThreads } from "src/hooks/useAgentThreads";
import { useStore } from "src/stores";
import { ThreadListItem } from "./ThreadListItem";
import { groupThreadsByDate } from "./utils/groupThreadsByDate";

export const ThreadHistoryMenu: React.FC<{
  onClose: () => void;
  onThreadSelect: (threadId: string) => void;
  historyAnchorEl: null | HTMLElement;
  isHistoryOpen: boolean;
}> = observer(function ThreadHistoryMenu({
  onClose,
  onThreadSelect,
  historyAnchorEl,
  isHistoryOpen,
}) {
  const { agentStore } = useStore();
  const {
    threads,
    isFetching: threadsLoading,
    hasNextPage: hasMoreThreads,
    fetchNextPage,
    isError,
    refetch,
  } = useAgentThreads();
  const updateThread = useUpdateThreadMutation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isThreadOptionsMenuOpen, setIsThreadOptionsMenuOpen] = useState(false);

  const handleThreadSelect = (threadId: string) => {
    onThreadSelect(threadId);
    onClose();
  };

  const handleRename = (threadId: string, newName: string) => {
    updateThread.mutate({
      threadId,
      metadata: { name: newName },
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    // Prevent MUI Menu from capturing key events in the search field
    event.stopPropagation();
  };

  const trimmedSearchQuery = searchQuery.trim();

  useEffect(() => {
    if (
      !isHistoryOpen ||
      !trimmedSearchQuery ||
      isError ||
      !hasMoreThreads ||
      threadsLoading
    ) {
      return;
    }

    void fetchNextPage();
  }, [
    fetchNextPage,
    hasMoreThreads,
    threadsLoading,
    isHistoryOpen,
    isError,
    trimmedSearchQuery,
  ]);

  const filteredThreads = useMemo(() => {
    const query = trimmedSearchQuery.toLowerCase();
    if (!query) {
      return threads;
    }
    return threads.filter((thread) => {
      const name = thread.metadata?.name
        ? String(thread.metadata.name).toLowerCase()
        : "untitled";
      return name.includes(query);
    });
  }, [threads, trimmedSearchQuery]);

  const groupedThreads = useMemo(
    () => groupThreadsByDate(filteredThreads),
    [filteredThreads]
  );

  const isLoadingSearchResults =
    Boolean(trimmedSearchQuery) &&
    threadsLoading &&
    hasMoreThreads &&
    filteredThreads.length === 0;
  const hasNoResults = trimmedSearchQuery && filteredThreads.length === 0;

  return (
    <Menu
      id="history-menu"
      anchorEl={historyAnchorEl}
      open={isHistoryOpen}
      onClose={() => {
        setSearchQuery("");
        setIsThreadOptionsMenuOpen(false);
        onClose();
      }}
      // Thread row actions render in a Popper portal, so allow focus to move into it.
      disableEnforceFocus={isThreadOptionsMenuOpen}
      MenuListProps={{
        "aria-labelledby": "history-button",
      }}
      slotProps={{
        paper: {
          style: {
            maxWidth: 400,
            minWidth: 400,
            maxHeight: 500,
          },
        },
      }}
      // For some reason if we don't have that, once the menu closes again, every other button on the page becomes unclickable.
      disablePortal
    >
      <div className="sticky top-0 z-10 bg-white px-2 pb-2">
        <TextField
          size="small"
          hiddenLabel
          fullWidth
          placeholder="Search threads..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          inputProps={{ "aria-label": "Search threads" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  fontSize="small"
                  sx={{ color: "rgba(100, 116, 139, 0.9)" }}
                />
              </InputAdornment>
            ),
          }}
          autoFocus
          data-testid="thread-search-input"
        />
      </div>
      {isError ? (
        <MenuItem onClick={() => refetch()}>
          Could not load history. Retry
        </MenuItem>
      ) : threadsLoading && threads.length === 0 ? (
        Array.from({ length: 3 }).map((_, index) => (
          <MenuItem key={`skeleton-${index}`} disabled>
            <Skeleton width="100%" height={20} />
          </MenuItem>
        ))
      ) : isLoadingSearchResults ? (
        <MenuItem disabled>
          <Typography color="text.secondary">Searching threads...</Typography>
        </MenuItem>
      ) : hasNoResults ? (
        <MenuItem disabled>
          <Typography color="text.secondary">
            No threads matching &quot;{trimmedSearchQuery}&quot;
          </Typography>
        </MenuItem>
      ) : threads.length === 0 ? (
        <MenuItem disabled>
          <Typography color="text.secondary">History is empty</Typography>
        </MenuItem>
      ) : (
        <>
          {groupedThreads.map(({ group, threads }) => (
            <React.Fragment key={group}>
              <ListSubheader
                disableSticky
                className="text-xs font-semibold uppercase tracking-wide"
              >
                {group}
              </ListSubheader>
              {threads.map((thread) => (
                <ThreadListItem
                  key={thread.thread_id}
                  thread={thread}
                  isActive={thread.thread_id === agentStore.currentThreadId}
                  onSelect={handleThreadSelect}
                  onRename={handleRename}
                  onOptionsMenuOpenChange={setIsThreadOptionsMenuOpen}
                />
              ))}
            </React.Fragment>
          ))}
          {hasMoreThreads && !trimmedSearchQuery && (
            <MenuItem
              onClick={() => fetchNextPage()}
              disabled={threadsLoading}
              className="justify-center"
            >
              {threadsLoading ? (
                <Typography color="text.secondary">Loading...</Typography>
              ) : (
                <Typography color="primary">Load more</Typography>
              )}
            </MenuItem>
          )}
        </>
      )}
    </Menu>
  );
});
